import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowUpRight,
  Bell,
  Building2,
  CheckCircle2,
  Eye,
  Info,
  MapPin,
  OctagonAlert,
  Plus,
  Search,
  Send,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, formatDate } from "../../utils";
import Button from "../../components/ui/Button";
import Input, { Label, Select, Textarea } from "../../components/ui/Input";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import TabLinks from "../../components/ui/TabLinks";
import Pagination from "../../components/ui/Pagination";
import ListFilters from "../../components/ui/ListFilters";
import useListQuery, { useSearchInput } from "../../hook/useListQuery";

const SENT_DEFAULTS = { page: 1, limit: 20, appId: "", tone: "", q: "" };

/**
 * Notifications to chosen accounts.
 *
 * Unlike an announcement, which every app page shows, a notification reaches
 * only the accounts picked here and lands in the bell on their app page — live,
 * if they have it open. The sent list shows how many of them have read it.
 */

const TONES = {
  info: { label: "Info", icon: Info, tile: "bg-brand-50 text-brand-600 ring-brand-200/70", solid: "bg-brand-600 text-white", chip: "bg-brand-50 text-brand-700 ring-brand-200/70" },
  success: { label: "Success", icon: CheckCircle2, tile: "bg-emerald-50 text-emerald-600 ring-emerald-200/70", solid: "bg-emerald-600 text-white", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200/70" },
  warning: { label: "Warning", icon: AlertTriangle, tile: "bg-amber-50 text-amber-600 ring-amber-200/80", solid: "bg-amber-500 text-white", chip: "bg-amber-50 text-amber-700 ring-amber-200/80" },
  critical: { label: "Critical", icon: OctagonAlert, tile: "bg-rose-50 text-rose-600 ring-rose-200/80", solid: "bg-rose-600 text-white", chip: "bg-rose-50 text-rose-700 ring-rose-200/80" },
};

const BLANK = { title: "", message: "", tone: "info", linkLabel: "", linkUrl: "", appId: "" };

// One notification as the bell shows it (see frontend/src/components/NotificationBell.jsx).
const BellItem = ({ n }) => {
  const t = TONES[n.tone] || TONES.info;
  const Icon = t.icon;
  return (
    <div className="flex gap-3 rounded-xl border border-ink-200/70 bg-brand-50/40 px-4 py-3.5">
      <span className={cx("grid h-9 w-9 shrink-0 place-items-center rounded-xl ring-1", t.tile)}>
        <Icon size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <p className="min-w-0 flex-1 text-[13px] font-semibold leading-snug text-ink-900">{n.title || "Notification title"}</p>
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-600" />
        </div>
        {n.message && <p className="mt-0.5 whitespace-pre-line text-[12.5px] leading-relaxed text-ink-600">{n.message}</p>}
        <div className="mt-2 flex items-center gap-3">
          <span className="text-[11px] text-ink-400">Just now</span>
          {n.linkUrl && (
            <span className="inline-flex items-center gap-0.5 text-[12px] font-semibold text-brand-600">
              {n.linkLabel || "Open"} <ArrowUpRight size={12} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const Notifications = () => {
  const { notifications, apps } = useAppServices();
  const toast = useToast();
  const confirm = useConfirm();

  // The sent list is paged and filtered on the server; the address holds the view.
  const [query, setQuery] = useListQuery(SENT_DEFAULTS);
  const [sentSearch, setSentSearch] = useSearchInput(query.q, (q) => setQuery({ q }));
  const [sentPage, setSentPage] = useState({ items: [], total: 0, page: 1, pages: 1, limit: SENT_DEFAULTS.limit });
  const sent = sentPage.items;
  const [accounts, setAccounts] = useState([]);
  const [appList, setAppList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [picked, setPicked] = useState([]);
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);

  // Loaded independently: the full app list is slow (every app with all its
  // content), and neither the sent list nor the account picker should wait
  // on it.
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  const loadSent = useCallback(async () => {
    const params = new URLSearchParams({ page: query.page, limit: query.limit });
    for (const key of ["appId", "tone", "q"]) if (query[key]) params.set(key, query[key]);
    const { response, error } = await notifications.List(params.toString());
    if (error) toast(error?.message || error?.error || "Could not load notifications", "error");
    const next = response && Array.isArray(response.items) ? response : { items: [], total: 0, page: 1, pages: 1, limit: query.limit };
    if (next.items.length === 0 && next.total > 0 && query.page > next.pages) setQuery({ page: next.pages });
    setSentPage(next);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.appId, query.tone, query.q]);

  useEffect(() => {
    loadSent();
  }, [loadSent]);

  useEffect(() => {
    notifications.Accounts().then(({ response }) => {
      setAccounts(Array.isArray(response) ? response : []);
      setLoadingAccounts(false);
    });
    apps.Get().then(({ response }) => setAppList(Array.isArray(response) ? response.filter((x) => x.appId) : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sentFiltered = Boolean(query.q || query.appId || query.tone);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  // Only accounts that have the chosen app can be sent an app-specific notification.
  const eligible = useMemo(
    () => (form.appId ? accounts.filter((a) => a.apps.some((x) => x.appId === form.appId)) : accounts),
    [accounts, form.appId],
  );

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return eligible;
    return eligible.filter((a) =>
      [a.name, a.locationId, a.companyId, ...a.apps.map((x) => x.appName)].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [eligible, search]);

  // Choosing an app drops picks that no longer qualify.
  useEffect(() => {
    const ok = new Set(eligible.map((a) => a.key));
    setPicked((prev) => prev.filter((k) => ok.has(k)));
  }, [eligible]);

  const allShownPicked = shown.length > 0 && shown.every((a) => picked.includes(a.key));
  const toggleAllShown = () =>
    setPicked((prev) =>
      allShownPicked ? prev.filter((k) => !shown.some((a) => a.key === k)) : [...new Set([...prev, ...shown.map((a) => a.key)])],
    );

  const startNew = () => {
    setForm(BLANK);
    setPicked([]);
    setSearch("");
    setComposing(true);
  };

  const send = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3) {
      toast("Give the notification a title of at least 3 characters.", "error");
      document.getElementById("nt-title")?.focus();
      return;
    }
    if (picked.length === 0) {
      toast("Choose at least one account to send it to.", "error");
      return;
    }
    setSending(true);
    const { error } = await notifications.Create({ ...form, recipients: picked });
    setSending(false);
    if (error) {
      toast(error?.message || "Could not send the notification", "error");
      return;
    }
    // Newest first, so it lands at the top of page one.
    if (query.page === 1) loadSent();
    else setQuery({ page: 1 });
    toast(`Sent to ${picked.length} account${picked.length === 1 ? "" : "s"}`);
    setComposing(false);
  };

  const remove = async (n) => {
    const ok = await confirm({
      title: "Delete this notification?",
      message: "It disappears from every recipient's bell straight away. This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    const { error } = await notifications.Delete(n._id);
    if (error) {
      toast(error?.message || "Could not delete the notification", "error");
      return;
    }
    loadSent();
    toast("Notification deleted");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink-900">Notifications</h1>
          <p className="mt-1 text-sm text-ink-500">
            Send a message to specific accounts. It appears in the bell on their app page — instantly if they have it open.
          </p>
        </div>
        {!composing && (
          <Button variant="gradient" onClick={startNew} icon={<Plus size={14} />}>
            New notification
          </Button>
        )}
      </div>

      {composing && (
        <form onSubmit={send} className="animate-fade-in overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card-elevated">
          <div className="flex items-center justify-between border-b border-ink-150 px-6 py-4">
            <h2 className="text-[15px] font-semibold text-ink-900">New notification</h2>
            <button type="button" onClick={() => setComposing(false)} className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700" aria-label="Close">
              <X size={16} />
            </button>
          </div>

          <div className="grid lg:grid-cols-2">
            {/* Message */}
            <div className="space-y-5 border-b border-ink-150 px-6 py-6 lg:border-b-0 lg:border-r">
              <div>
                <Label>Type</Label>
                <div className="mt-2 grid grid-cols-4 gap-2" role="radiogroup" aria-label="Type">
                  {Object.entries(TONES).map(([id, t]) => {
                    const Icon = t.icon;
                    const on = form.tone === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => set("tone")(id)}
                        className={cx(
                          "flex flex-col items-center gap-1.5 rounded-xl border px-2 py-2.5 text-[12px] font-medium transition-all",
                          on ? cx("border-transparent ring-1", t.chip) : "border-ink-200 text-ink-600 hover:bg-ink-50",
                        )}
                      >
                        <span className={cx("grid h-6 w-6 place-items-center rounded-md", t.solid)}>
                          <Icon size={13} />
                        </span>
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="nt-title" required>Title</Label>
                <Input id="nt-title" className="mt-1.5" maxLength={120} value={form.title} onChange={(e) => set("title")(e.target.value)} placeholder="e.g. Your Twilio balance is running low" />
              </div>

              <div>
                <Label htmlFor="nt-message" hint="optional">Message</Label>
                <Textarea id="nt-message" className="mt-1.5" rows={4} maxLength={1000} value={form.message} onChange={(e) => set("message")(e.target.value)} placeholder="Top up your Twilio account to keep messages sending." />
                <p className="mt-1 text-right text-[11px] tabular-nums text-ink-400">{form.message.length}/1000</p>
              </div>

              <div className="grid gap-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
                <div>
                  <Label htmlFor="nt-link-label" hint="optional">Button text</Label>
                  <Input id="nt-link-label" className="mt-1.5" maxLength={40} value={form.linkLabel} onChange={(e) => set("linkLabel")(e.target.value)} placeholder="Open" />
                </div>
                <div>
                  <Label htmlFor="nt-link-url" hint="optional">Button link</Label>
                  <Input id="nt-link-url" className="mt-1.5" maxLength={500} value={form.linkUrl} onChange={(e) => set("linkUrl")(e.target.value)} placeholder="https://… or #support" />
                  <TabLinks value={form.linkUrl} label={form.linkLabel} onPick={(v) => setForm((f) => ({ ...f, ...v }))} />
                </div>
              </div>

              <div>
                <p className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                  <Eye size={12} /> How it appears in their bell
                </p>
                <BellItem n={form} />
              </div>
            </div>

            {/* Recipients */}
            <div className="flex min-h-0 flex-col px-6 py-6">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="nt-app">Show on</Label>
                  <Select id="nt-app" className="mt-1.5" value={form.appId} onChange={(e) => set("appId")(e.target.value)}>
                    <option value="">Any of our apps they open</option>
                    {appList.map((a) => (
                      <option key={a.appId} value={a.appId}>
                        Only {a.appName}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="nt-search">Recipients</Label>
                  <div className="relative mt-1.5">
                    <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                    <Input id="nt-search" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search accounts" />
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between gap-2 text-[12px]">
                <button type="button" onClick={toggleAllShown} disabled={shown.length === 0} className="font-medium text-brand-600 hover:underline disabled:text-ink-300">
                  {allShownPicked ? "Clear" : "Select"} {search ? "these" : "all"} {shown.length}
                </button>
                <span className={cx("inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ring-1", picked.length ? "bg-brand-50 text-brand-700 ring-brand-200/70" : "bg-ink-100 text-ink-500 ring-ink-200/70")}>
                  <Users size={11} /> {picked.length} selected
                </span>
              </div>

              <ul className="mt-2 max-h-[24rem] min-h-[12rem] flex-1 space-y-1 overflow-y-auto rounded-xl border border-ink-200 p-1.5">
                {loadingAccounts ? (
                  [0, 1, 2, 3].map((i) => <li key={i} className="h-12 animate-pulse rounded-lg bg-ink-100" />)
                ) : shown.length === 0 ? (
                  <li className="px-4 py-10 text-center text-[12.5px] text-ink-500">
                    {eligible.length === 0 ? "No account has this app installed." : "No accounts match that search."}
                  </li>
                ) : (
                  shown.map((a) => {
                    const on = picked.includes(a.key);
                    return (
                      <li key={a.key}>
                        <label className={cx("flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2.5 transition-colors", on ? "bg-brand-50/70" : "hover:bg-ink-50")}>
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-ink-300 text-brand-600"
                            checked={on}
                            onChange={(e) => setPicked((prev) => (e.target.checked ? [...prev, a.key] : prev.filter((k) => k !== a.key)))}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                              {a.level === "agency" ? <Building2 size={12} className="shrink-0 text-violet-500" /> : <MapPin size={12} className="shrink-0 text-ink-400" />}
                              <span className="truncate text-[13px] font-medium text-ink-800">{a.name}</span>
                              {a.level === "agency" && <span className="rounded bg-violet-50 px-1 text-[10px] font-semibold uppercase text-violet-600">Agency</span>}
                            </span>
                            <span className="mt-0.5 block truncate font-mono text-[10.5px] text-ink-400">{a.locationId || a.companyId}</span>
                            <span className="mt-1 flex flex-wrap gap-1">
                              {a.apps.map((x) => (
                                <span key={x.appId} className="rounded-md bg-ink-100 px-1.5 py-px text-[10.5px] font-medium text-ink-600">
                                  {x.appName}
                                </span>
                              ))}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-ink-150 bg-ink-50/50 px-6 py-3.5">
            <p className="mr-auto text-[12px] text-ink-500">
              {picked.length ? `Goes to ${picked.length} account${picked.length === 1 ? "" : "s"}${form.appId ? `, on ${appList.find((a) => a.appId === form.appId)?.appName}` : ""}.` : "Pick at least one account."}
            </p>
            <Button type="button" variant="ghost" onClick={() => setComposing(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="gradient" loading={sending} icon={!sending && <Send size={14} />}>
              Send notification
            </Button>
          </div>
        </form>
      )}

      <div className="overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card">
        {(sentPage.total > 0 || sentFiltered) && (
          <ListFilters
            search={sentSearch}
            onSearch={setSentSearch}
            placeholder="Search title, message or account"
            filters={[
              {
                label: "App",
                value: query.appId,
                onChange: (appId) => setQuery({ appId }),
                options: [["", "All apps"], ...appList.map((a) => [a.appId, a.appName || a.appId])],
                className: "w-48",
              },
              {
                label: "Type",
                value: query.tone,
                onChange: (tone) => setQuery({ tone }),
                options: [["", "All types"], ...Object.entries(TONES).map(([id, t]) => [id, t.label])],
                className: "w-32",
              },
            ]}
            onClear={() => setQuery({ q: "", appId: "", tone: "" })}
          />
        )}
        {loading ? (
          <div className="space-y-3 p-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-ink-100" />
            ))}
          </div>
        ) : sent.length === 0 && sentFiltered ? (
          <div className="px-6 py-14 text-center">
            <h3 className="text-[14px] font-semibold text-ink-900">No notifications match</h3>
            <p className="mt-1 text-[13px] text-ink-500">Try another search, app or type.</p>
          </div>
        ) : sent.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
              <Bell size={24} />
            </span>
            <h3 className="mt-5 text-[15px] font-semibold text-ink-900">No notifications sent yet</h3>
            <p className="mx-auto mt-1.5 max-w-sm text-[13px] leading-relaxed text-ink-500">
              Reach specific accounts — a low balance, an expiring key, a fix that affects only them.
            </p>
            {!composing && (
              <Button variant="secondary" size="sm" className="mt-5" onClick={startNew} icon={<Plus size={14} />}>
                Send the first one
              </Button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-ink-150">
            {sent.map((n) => {
              const t = TONES[n.tone] || TONES.info;
              const Icon = t.icon;
              const pct = n.recipientCount ? Math.round((n.readCount / n.recipientCount) * 100) : 0;
              return (
                <li key={n._id} className="flex items-start gap-4 px-5 py-4 transition-colors hover:bg-ink-50/60">
                  <span className={cx("mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl", t.solid)}>
                    <Icon size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-semibold text-ink-900">{n.title}</p>
                    {n.message && <p className="mt-0.5 line-clamp-1 text-[13px] text-ink-500">{n.message}</p>}
                    <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-ink-400">
                      <span title={n.recipients.map((r) => r.name).join(", ")}>
                        To {n.recipients.length === 1 ? n.recipients[0].name : `${n.recipientCount} accounts`}
                      </span>
                      <span>{n.appName ? `On ${n.appName}` : "Any app"}</span>
                      <span>{formatDate(n.createdAt)}</span>
                    </p>
                  </div>
                  <div className="w-36 shrink-0">
                    <div className="flex items-baseline justify-between text-[11.5px]">
                      <span className="font-medium text-ink-700">
                        {n.readCount}/{n.recipientCount} read
                      </span>
                      <span className="tabular-nums text-ink-400">{pct}%</span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-ink-100">
                      <div className="h-full rounded-full bg-gradient-to-r from-brand-500 to-violet-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <button type="button" onClick={() => remove(n)} title="Delete" aria-label="Delete" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600">
                    <Trash2 size={15} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {!loading && (
          <Pagination
            page={sentPage.page}
            pages={sentPage.pages}
            total={sentPage.total}
            limit={sentPage.limit}
            noun={sentPage.total === 1 ? "notification" : "notifications"}
            onPage={(page) => setQuery({ page })}
            onLimit={(limit) => setQuery({ limit })}
          />
        )}
      </div>
    </div>
  );
};

export default Notifications;
